import { getTimeZones } from "@vvo/tzdb";

export const setFormatDate = (date: string | Date): Date => {
	return typeof date === "string" ? new Date(date) : date;
};

export const createISOStringFromDate = (date: string): string => {
	const getDate = new Date(date).setUTCDate(new Date(date).getDate()); //set UTC date
	const getYear = new Date(getDate).setUTCFullYear(new Date(date).getFullYear()); //set UTC date
	const getMonth = new Date(getYear).setUTCMonth(new Date(date).getMonth()); //set UTC date
	const newDate = new Date(getMonth).setUTCHours(0, 0, 0, 0); // set UTC Hours
	return new Date(newDate).toISOString();
};

export const createISOStringFromDateWithHours = (date: string): string => {
	const getDate = new Date(date).setUTCDate(new Date(date).getDate()); //set UTC date
	const getYear = new Date(getDate).setUTCFullYear(new Date(date).getFullYear()); //set UTC date
	const getMonth = new Date(getYear).setUTCMonth(new Date(date).getMonth()); //set UTC date
	const newDate = new Date(getMonth).setUTCHours(new Date(date).getHours(), new Date(date).getMinutes(), 0, 0); // set UTC Hours
	return new Date(newDate).toISOString();
};

export const formatDateYYYYMMDD = (date: Date): string => {
	const localDate = new Date(date);
	const year = localDate.getFullYear();
	const month = (localDate.getMonth() + 1).toString().padStart(2, "0");
	const day = localDate.getDate().toString().padStart(2, "0");
	return `${year}-${month}-${day}`;
};

export const createISOStringFromDateWithoutHours = (date: string): string => {
	const getDate = new Date(date).setUTCDate(new Date(date).getDate()); //set UTC date
	const getYear = new Date(getDate).setUTCFullYear(new Date(date).getFullYear()); //set UTC date
	const getMonth = new Date(getYear).setUTCMonth(new Date(date).getMonth()); //set UTC date
	const newDate = new Date(getMonth).setUTCHours(0, 0, 0, 0); // set UTC Hours
	return new Date(newDate).toISOString().split("T")[0];
};

export const createISOStringFromDateWithoutMilleseconds = (date: string): string => {
	const getDate = new Date(date).setUTCDate(new Date(date).getDate()); //set UTC date
	const getYear = new Date(getDate).setUTCFullYear(new Date(date).getFullYear()); //set UTC date
	const getMonth = new Date(getYear).setUTCMonth(new Date(date).getMonth()); //set UTC date
	const newDate = new Date(getMonth).setUTCHours(0, 0, 0, 0); // set UTC Hours
	return new Date(newDate).toISOString().replace(/\.\d+/, "");
};

export const correctConvertToISODate = (date: Date): Date => {
	//https://stackoverflow.com/a/45779965
	/*When you create a date with new Date(str) it creates a date object
	 	with a time zone. toISOString() makes it zero UTC offset, as denoted by the suffix "Z".
		getTimezoneOffset returns number of minutes and new Date expects number 
		//of milliseconds since January 1 1970 00:00:00 UTC, so multiplying by 60000 provides needed adjustment.
	*/
	return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
};

//General conversion

export const convertLocalTimeToUTCForBE = (localDate: Date): string => {
	// Extract year, month, day, etc. from the UTC timestamp
	const year = new Date(localDate).getUTCFullYear();
	const monthIndex = new Date(localDate).getUTCMonth();
	const day = new Date(localDate).getUTCDate();
	const hours = new Date(localDate).getUTCHours();
	const minutes = new Date(localDate).getUTCMinutes();
	const seconds = new Date(localDate).getUTCSeconds();
	const milliseconds = new Date(localDate).getUTCMilliseconds();

	// Create a new Date object with the extracted components
	const utcDate = new Date(Date.UTC(year, monthIndex, day, hours, minutes, seconds, milliseconds));

	return utcDate.toISOString(); // Convert to ISO string
};

export const convertUTCDateInLocalTimeForFE = (utcDate: Date): string => {
	const date = new Date(utcDate);
	const timezoneOffset = date.getTimezoneOffset();
	const localDate = new Date(date.getTime() - timezoneOffset * 60000);
	return localDate.toISOString();
};

//For milestone dates

export const mergeDateAndTime = (date: Date, time: string): Date => {
	let hours: number = +time.split(":")[0];
	hours = hours === 24 ? +"00" : hours;

	const minutes: number = +time.split(":")[1];

	date.setHours(12);
	date.setUTCHours(hours);
	date.setUTCMinutes(minutes);

	return convertDateToSemiUTC(date);
};

export const convertMilestoneDateToUTC = (date: Date, legalEntityTimezone: string): string => {
	return calculateUTCDateBySubtractingOffset(date, getOffsetFromLegalEntityTimezone(legalEntityTimezone, date));
};

export const getOffsetFromLegalEntityTimezone = (timezone: string, milestoneDate: Date): number => {
	if (Number.isNaN(milestoneDate.getTime())) return 0;

	const timeZoneName = Intl.DateTimeFormat("en", {
		timeZoneName: "shortOffset",
		timeZone: timezone
	})
		.formatToParts(milestoneDate)
		.find(part => part.type === "timeZoneName")?.value;

	const offsetMatch = timeZoneName?.match(/^GMT(?:([+-])(\d{1,2})(?::(\d{2}))?)?$/);

	if (!offsetMatch?.[1] || !offsetMatch[2]) return 0;

	const [, sign, hours, minutes = "0"] = offsetMatch;
	const offset = Number(hours) + Number(minutes) / 60;
	return sign === "-" ? -offset : offset;
};

export const calculateUTCDateBySubtractingOffset = (date: Date, offset: number): string => {
	return correctConvertToISODate(new Date(date.getTime() - offset * 60 * 60 * 1000)).toISOString();
};

export const convertDateToSemiUTC = (date: Date): Date => {
	return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
};

export const convertTo24HourFormat = (date: Date): string => {
	return date.toLocaleTimeString("US", {
		hourCycle: "h23",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit"
	});
};

/**
 * Normalize a Date | string to a Date.
 * - Treats "YYYY-MM-DD" as a local date (avoids UTC drift).
 * - Falls back to native parsing for ISO timestamps and other strings.
 */
export function toDate(input: Date | string): Date {
	if (input instanceof Date) return input;

	const str = input?.trim?.() ?? "";

	// "YYYY-MM-DD" -> local date (midnight local time)
	const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
	if (dateOnlyMatch) {
		const [, y, m, d] = dateOnlyMatch;
		return new Date(Number(y), Number(m) - 1, Number(d));
	}

	// Fallback: native parsing (e.g., ISO timestamps)
	return new Date(str);
}

/**
 * Return ordinal string for a day number.
 * Examples: 1 -> "1st", 2 -> "2nd", 3 -> "3rd", 4 -> "4th", 11 -> "11th"
 */
export function getOrdinal(n: number): string {
	const v = n % 100;
	if (v >= 11 && v <= 13) return `${n}th`;
	switch (n % 10) {
		case 1:
			return `${n}st`;
		case 2:
			return `${n}nd`;
		case 3:
			return `${n}rd`;
		default:
			return `${n}th`;
	}
}

/**
 * Format an ordinal date like "December 8th".
 * - Input: Date | string
 * - Output: '' if input is invalid
 * - Month name via en-GB locale (required)
 */
export function getOrdinalDate(value: Date | string): string {
	const dateObject = toDate(value);
	if (Number.isNaN(dateObject.getTime())) return "";

	const month = dateObject.toLocaleString("en-GB", { month: "long" });
	const dayOrdinal = getOrdinal(dateObject.getDate());

	return `${month} ${dayOrdinal}`;
}

/**
 * Format time as "HH:mm" (24-hour), zero-padded.
 * - Input: Date | string
 * - Output: '' if input is invalid
 */
export function getTimeFromDate(value: Date | string): string {
	const dateObject = toDate(value);
	if (Number.isNaN(dateObject.getTime())) return "";

	const hours = String(dateObject.getHours()).padStart(2, "0");
	const minutes = String(dateObject.getMinutes()).padStart(2, "0");

	return `${hours}:${minutes}`;
}

export const timeZoneAbbreviation = (
	localTimeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone
): string => {
	const timeZone = getTimeZones().find(({ name }) => name === localTimeZone);

	return timeZone?.abbreviation ?? "GMT";
};

export function setupUTCOffset(): string {
	const sign = timeZoneRawOffsetMinutes() >= 0 ? "+" : "-";
	const offset =
		timeZoneRawOffsetMinutes() >= 0
			? `${sign}${convertDecimalToTime(timeZoneRawOffsetMinutes() / 60)}`
			: `${sign}${convertDecimalToTime(-timeZoneRawOffsetMinutes() / 60)}`;
	return offset;
}

export const timeZoneRawOffsetMinutes = (): number => {
	const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	const timeZone = getTimeZones({ includeUtc: true }).find(({ name }) => name === localTimeZone);

	return timeZone?.rawOffsetInMinutes ?? 0;
};

export function convertDecimalToTime(decimalTime: number): string {
	const hours = Math.floor(decimalTime);

	const minutes = Math.round((decimalTime - hours) * 60);

	const formattedHours = String(hours).padStart(2, "0");
	const formattedMinutes = String(minutes).padStart(2, "0");

	return `${formattedHours}:${formattedMinutes}`;
}
